import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { DomController } from '@ionic/angular';

import { catchError, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { Address } from '../../core/models/address.model';
import { ISO_FORMAT } from '../../core/models/date-time-format.model';
import { ItemAvailableDateTime } from '../../core/models/item-available-date-time.model';

import { CalendarMonth, DateTimeService } from '../../core/services/date-time.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { OverlayService } from '../../core/services/overlay.service';
import { SettingsService } from '../../core/services/settings.service';
import { PlatformService } from '../../core/services/ssr/platform.service';
import { VendorsService } from '../../core/services/vendors.service';

import { Helpers } from '../../core/helpers';

import { ItemAvailabilityDateTimePickerModalPageAnimations } from './item-availability-date-time-picker-modal.animations';

import Swiper from 'swiper';
import { SwiperComponent } from 'swiper/angular';

export const ItemAvailabilityDateTimePickerModalPageIdentifier = 'item-availability-date-time-picker-modal';

// The height of the date picker is fixed because we show the same amount
// of days for each month (for months with less day we'd show more daysAfterEnd)
const CALENDAR_DATE_PICKER_HEIGHT_IN_PX = 516;
const CALENDAR_TIME_PICKER_FIXED_SIZE_IN_PX = 208;
const CALENDAR_ERROR_PICKER_FIXED_SIZE_IN_PX = 296;
const CALENDAR_MIN_HEIGHT_IN_PX = 150;
const CALENDAR_DAY_VIEW_ITEM_HEIGHT_IN_PX = 36;
const CALENDAR_MONTH_VIEW_ITEM_HEIGHT_IN_PX = 44;

interface HourMinuteValue {
  text: string;
  available: boolean;
  isOrderForNow: boolean;
  dateTimeValue: string;
}

export interface ItemAvailabilityDateTimePickerModalPageParams {
  vendorId: number;
  menuItemId: number;
  selectedAddress: Address;
  selectedDateTime: string;
  isOrderForNowSelected: boolean;
  isOrderForNowAvailable: boolean;
}

export interface ItemAvailabilityDateTimePickerModalPageResult {
  hasChangedData: boolean;
  selectedDateTime: string;
  isOrderForNowSelected: boolean;
}

type NextAvailableTimes = {
  dateIsoString: string;
  times: Array<HourMinuteValue>;
};

@Component({
  selector: 'app-item-availability-date-time-picker-modal',
  templateUrl: 'item-availability-date-time-picker-modal.page.html',
  styleUrls: ['item-availability-date-time-picker-modal.page.scss'],
  animations: [...ItemAvailabilityDateTimePickerModalPageAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class ItemAvailabilityDateTimePickerModalPage implements OnInit, OnDestroy {
  @Input()
  public params: ItemAvailabilityDateTimePickerModalPageParams;

  @ViewChild('datePickerSwiper')
  public datePickerSwiper?: SwiperComponent;

  public isReady: boolean;

  public showDatePicker: boolean;
  public datePicker: Array<CalendarMonth>;
  public selectedMonthIndex = -1;
  public selectedDayIndex = -1;
  public canCheckPrevMonth: boolean;
  public canCheckNextMonth: boolean;
  public isLoadingAvailableTimes: boolean;
  public nextAvailableTimes: NextAvailableTimes;
  public nextAvailableTimesCache: Record<string, ItemAvailableDateTime> = {};

  public shouldShowLoadingErrorMessage: boolean;
  public shouldShowItemNotAvailableSoonMessage: boolean;
  public shouldShowItemNotAvailableBeforeDateMessage: boolean;

  public isOrderForNowAvailable: boolean;
  public newSelectedDateTime: string;
  public newIsOrderForNowSelected: boolean;

  public swiperScrollingDirection: 'ltr' | 'rtl';
  public calendarItemHeight = CALENDAR_DAY_VIEW_ITEM_HEIGHT_IN_PX;
  public calendarContainerHeight = CALENDAR_MIN_HEIGHT_IN_PX;

  public selectedDateTime: string;
  public isOrderForNowSelected: boolean;

  private areaId: number;
  private vendorId: number;
  private menuItemId: number;

  private onSelectedDateChanged$ = new Subject<string>();
  private onSelectedDateConfirmed$ = new Subject<string>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private ngZone: NgZone,
    private domCtrl: DomController,
    private modalService: ModalService,
    private loggerService: LoggerService,
    private vendorsService: VendorsService,
    private overlayService: OverlayService,
    private dateTimeService: DateTimeService,
    private platformService: PlatformService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.isReady = false;

    this.areaId = this.params?.selectedAddress?.area.areaId;
    this.vendorId = this.params?.vendorId;
    this.menuItemId = this.params?.menuItemId;
    this.selectedDateTime = this.params?.selectedDateTime;
    this.isOrderForNowSelected = this.params?.isOrderForNowSelected;
    this.isOrderForNowAvailable = this.params?.isOrderForNowAvailable;

    this.swiperScrollingDirection = this.settingsService.getLanguageDirection();
    this.datePicker = this.dateTimeService.getCalendarForCurrentMonthAndNextTwoMonths();

    if (!this.vendorId || !this.menuItemId || !this.areaId) {
      void this.platformService.wait(300).then(() => {
        const errorMessage = this.translateService.instant('ERROR_MESSAGE.DEFAULT_MESSAGE') as string;
        this.loggerService.info({ component: 'ItemAvailabilityDateTimePickerModal', message: 'some mandatory params are undefined' });
        this.overlayService.showToast({ message: errorMessage, showCloseButton: true, type: 'error' });
        void this.dismiss();
      });
      return;
    }

    // This stream only preloads the available times for the selected date as soon as the
    // user selects a date in the calendar but before they press the continue button
    this.onSelectedDateChanged$
      .pipe(
        switchMap((nextAvailableDate) => this.loadAvailableTimesForDate(nextAvailableDate).pipe(catchError(() => of(null)))),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    this.onSelectedDateConfirmed$
      .pipe(
        tap(() => {
          this.showDatePicker = false;
          this.newSelectedDateTime = null;
          this.isLoadingAvailableTimes = true;
          this.shouldShowLoadingErrorMessage = false;
          this.shouldShowItemNotAvailableSoonMessage = false;
          this.shouldShowItemNotAvailableBeforeDateMessage = false;
        }),
        switchMap((selectedDate) =>
          this.loadAvailableTimesForDate(selectedDate).pipe(
            tap((nextAvailableDateDetails) => {
              const selectedDateIsoString = this.datePicker[this.selectedMonthIndex].days[this.selectedDayIndex].valueIsoString;

              this.shouldShowItemNotAvailableSoonMessage =
                selectedDateIsoString !== nextAvailableDateDetails.dateIsoString && !nextAvailableDateDetails.times?.length;
              this.shouldShowItemNotAvailableBeforeDateMessage =
                selectedDateIsoString !== nextAvailableDateDetails.dateIsoString && !!nextAvailableDateDetails.times?.length;

              this.nextAvailableTimes = nextAvailableDateDetails;
              this.isLoadingAvailableTimes = false;

              // This is important to animate any changes in the height of the container
              this.domCtrl.write(() => {
                this.calendarItemHeight = CALENDAR_DAY_VIEW_ITEM_HEIGHT_IN_PX;
                this.calendarContainerHeight =
                  this.shouldShowLoadingErrorMessage ||
                  this.shouldShowItemNotAvailableBeforeDateMessage ||
                  this.shouldShowItemNotAvailableSoonMessage
                    ? CALENDAR_ERROR_PICKER_FIXED_SIZE_IN_PX
                    : Math.ceil(this.nextAvailableTimes.times.length / 4) * this.calendarItemHeight + CALENDAR_TIME_PICKER_FIXED_SIZE_IN_PX;
              });
            }),
            catchError((error: HttpErrorResponse) => {
              this.loggerService.info({
                component: 'ItemAvailabilityDateTimePicker',
                message: "couldn't load item available date/times",
                details: { error },
              });

              this.shouldShowLoadingErrorMessage = true;
              this.domCtrl.write(() => (this.calendarContainerHeight = CALENDAR_ERROR_PICKER_FIXED_SIZE_IN_PX));

              return of(null);
            }),
          ),
        ),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    const initialDateTime = this.selectedDateTime
      ? this.dateTimeService.parseIso(this.selectedDateTime).startOf('day').format(ISO_FORMAT)
      : this.dateTimeService.now.startOf('day').format(ISO_FORMAT);

    const { monthIndex, dayIndex } = this.getDatePickerMonthAndDayIndexForDate(initialDateTime);

    if (this.selectedDateTime || this.isOrderForNowSelected) {
      if (monthIndex >= 0 && dayIndex >= 0) {
        this.updateSelectedDatePickerIndex({ selectedMonthIndex: monthIndex, selectedDayIndex: dayIndex });
        this.onSelectedDateConfirmed$.next(initialDateTime);
      }
    } else {
      this.updateSelectedDatePickerIndex({ selectedMonthIndex: monthIndex });
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onShowNextMonth(): void {
    if (this.isMaxMonth(this.datePicker[this.selectedMonthIndex]) || !this.datePickerSwiper?.swiperRef) {
      return;
    }

    this.datePickerSwiper.swiperRef.slideNext();
  }

  public onShowPrevMonth(): void {
    if (this.isMinMonth(this.datePicker[this.selectedMonthIndex]) || !this.datePickerSwiper?.swiperRef) {
      return;
    }

    this.datePickerSwiper.swiperRef.slidePrev();
  }

  public onUpdateSelectedDate(selectedDateIso: string): void {
    if (
      this.selectedMonthIndex >= 0 &&
      this.selectedDayIndex >= 0 &&
      this.datePicker[this.selectedMonthIndex].days[this.selectedDayIndex].valueIsoString === selectedDateIso
    ) {
      return;
    }

    this.selectedDayIndex = this.datePicker[this.selectedMonthIndex].days.findIndex((day) => day.valueIsoString === selectedDateIso);
    this.onSelectedDateChanged$.next(selectedDateIso);
  }

  public onUpdateSelectedDateFromDifferentMonth(selectedDateIso: string): void {
    const newIndex = this.getDatePickerIndexForDate(selectedDateIso);

    // This is important if the user clicks on the "daysAfterEnd" from
    // the last month as we don't allow selecting those days
    if (newIndex < 0 || newIndex > this.datePicker.length) {
      return;
    }

    if (newIndex < this.selectedMonthIndex) {
      this.onShowPrevMonth();
    } else if (newIndex > this.selectedMonthIndex) {
      this.onShowNextMonth();
    }

    this.onUpdateSelectedDate(selectedDateIso);
  }

  public onUpdateSelectedDateTime(hourMinuteValue: HourMinuteValue): void {
    this.newSelectedDateTime = hourMinuteValue.isOrderForNow ? undefined : hourMinuteValue.dateTimeValue;
    this.newIsOrderForNowSelected = hourMinuteValue.isOrderForNow;
  }

  public onGoBack(): void {
    if (this.isLoadingAvailableTimes) {
      return;
    }

    if (this.showDatePicker) {
      this.onDismiss();
      return;
    }

    this.resetDatePicker({ selectedDayIndex: this.selectedDayIndex });
  }

  public onHandleSubmit(): void {
    if (this.showDatePicker) {
      if (this.selectedMonthIndex < 0 || this.selectedDayIndex < 0) {
        return;
      }

      const selectedDay = this.datePicker[this.selectedMonthIndex].days[this.selectedDayIndex];
      this.onSelectedDateConfirmed$.next(selectedDay.valueIsoString);
    } else {
      if (!this.newSelectedDateTime && !this.newIsOrderForNowSelected) {
        return;
      }

      void this.dismiss({
        hasChangedData:
          !!this.isOrderForNowSelected !== !!this.newIsOrderForNowSelected ||
          this.dateTimeService.hasChangedDateOrTime(this.selectedDateTime, this.newSelectedDateTime),
        selectedDateTime: this.newSelectedDateTime,
        isOrderForNowSelected: this.newIsOrderForNowSelected,
      });
    }
  }

  public onHandleLoadingError(): void {
    const selectedDay =
      this.selectedMonthIndex >= 0 && this.selectedDayIndex >= 0
        ? this.datePicker[this.selectedMonthIndex].days[this.selectedDayIndex].valueIsoString
        : undefined;

    if (selectedDay) {
      this.onSelectedDateConfirmed$.next(selectedDay);
    }
  }

  public onHandleItemNotAvailableBeforeDateError(): void {
    const selectedDay = this.nextAvailableTimes?.dateIsoString || undefined;

    if (selectedDay) {
      this.onUpdateSelectedDateFromDifferentMonth(selectedDay);
      this.onSelectedDateConfirmed$.next(selectedDay);
    }
  }

  public onHandleItemNotAvailableSoonError(): void {
    const selectedDay = this.nextAvailableTimes?.dateIsoString || undefined;

    if (selectedDay) {
      this.onUpdateSelectedDateFromDifferentMonth(selectedDay);
      this.onSelectedDateConfirmed$.next(selectedDay);
    }
  }

  public onDismiss(results?: ItemAvailabilityDateTimePickerModalPageResult): void {
    void this.dismiss(results);
  }

  public onDatePickerSwiperLoaded(datePickerSwiper: Swiper): void {
    if (datePickerSwiper) {
      void this.platformService.wait(50).then(() => datePickerSwiper.update());
    }
  }

  public onDatePickerActiveIndexChange(): void {
    const newIndex = this.datePickerSwiper?.swiperRef?.activeIndex;

    if (newIndex >= 0) {
      this.ngZone.run(() => {
        this.updateSelectedDatePickerIndex({ selectedMonthIndex: newIndex });
      });
    }
  }

  private updateSelectedDatePickerIndex({
    selectedMonthIndex,
    selectedDayIndex,
  }: {
    selectedMonthIndex: number;
    selectedDayIndex?: number;
  }): void {
    this.selectedMonthIndex = selectedMonthIndex;
    this.canCheckPrevMonth = !this.isMinMonth(this.datePicker[this.selectedMonthIndex]);
    this.canCheckNextMonth = !this.isMaxMonth(this.datePicker[this.selectedMonthIndex]);
    this.resetDatePicker({ selectedDayIndex });
  }

  private resetDatePicker({ selectedDayIndex }: { selectedDayIndex: number }): void {
    this.showDatePicker = true;
    this.selectedDayIndex = selectedDayIndex >= 0 ? selectedDayIndex : -1;
    this.nextAvailableTimes = undefined;
    this.shouldShowLoadingErrorMessage = false;
    this.shouldShowItemNotAvailableSoonMessage = false;
    this.shouldShowItemNotAvailableBeforeDateMessage = false;

    // This is important to animate any changes in the height of the container
    this.domCtrl.write(() => {
      this.calendarItemHeight = CALENDAR_MONTH_VIEW_ITEM_HEIGHT_IN_PX;
      this.calendarContainerHeight = CALENDAR_DATE_PICKER_HEIGHT_IN_PX;
    });
  }

  private loadAvailableTimesForDate(selectedDateIsoString: string): Observable<NextAvailableTimes> {
    const dataSource$ =
      this.nextAvailableTimesCache && this.nextAvailableTimesCache[selectedDateIsoString]
        ? of(this.nextAvailableTimesCache[selectedDateIsoString])
        : this.vendorsService.getMenuItemAvailableTimes({
            areaId: this.areaId,
            vendorId: this.vendorId,
            menuItemId: this.menuItemId,
            dateTime: selectedDateIsoString,
          });

    return dataSource$.pipe(
      map((availableTimes) => {
        this.nextAvailableTimesCache[selectedDateIsoString] = availableTimes;

        const nextAvailableTimes: Array<HourMinuteValue> = availableTimes.times.map((availableTime) => ({
          text: `${Helpers.padNumber(availableTime.hour)}:${Helpers.padNumber(availableTime.minute)}`,
          available: !!availableTime.available,
          dateTimeValue: this.dateTimeService
            .parseIso(availableTimes.date)
            .startOf('day')
            .hour(availableTime.hour)
            .minute(availableTime.minute)
            .format(ISO_FORMAT),
          isOrderForNow: false,
        }));

        if (this.dateTimeService.isToday(availableTimes.date) && this.isOrderForNowAvailable) {
          nextAvailableTimes.unshift({
            text: this.translateService.instant('NOW') as string,
            available: true,
            dateTimeValue: undefined,
            isOrderForNow: true,
          });
        }

        return {
          dateIsoString: availableTimes.date,
          times: nextAvailableTimes,
        };
      }),
    );
  }

  private dismiss(results?: ItemAvailabilityDateTimePickerModalPageResult): Promise<boolean> {
    return this.modalService.dismissModal({ id: ItemAvailabilityDateTimePickerModalPageIdentifier, data: results });
  }

  private getDatePickerIndexForDate(targetDateTimeIsoString: string): number {
    return this.datePicker.findIndex(
      (calendar) =>
        calendar.month === this.dateTimeService.getMonthFromDateTimeIso(targetDateTimeIsoString) &&
        calendar.year === this.dateTimeService.getYearFromDateTimeIso(targetDateTimeIsoString),
    );
  }

  private getDatePickerMonthAndDayIndexForDate(targetDateTimeIsoString: string): { monthIndex: number; dayIndex: number } {
    const monthIndex = this.getDatePickerIndexForDate(targetDateTimeIsoString);

    if (monthIndex < 0) {
      return { monthIndex: -1, dayIndex: -1 };
    }

    const dayIndex = this.datePicker[monthIndex].days.findIndex((day) => day.valueIsoString === targetDateTimeIsoString);

    if (dayIndex < 0) {
      return { monthIndex: -1, dayIndex: -1 };
    }

    return { monthIndex, dayIndex };
  }

  private isMinMonth(selectedMonth: CalendarMonth): boolean {
    return !selectedMonth.isPrevAvailable;
  }

  private isMaxMonth(selectedMonth: CalendarMonth): boolean {
    return !selectedMonth.isNextAvailable;
  }
}
